// The shell: what is always on the screen, and which screen the address bar
// names.
//
// THE DEFAULT LANDING VIEW IS `Interplant Transfers`, NOT THE DASHBOARD. That
// is the source's own setting and it is easy to assume otherwise -- an
// application with a screen called Dashboard looks like it should open on it.
// It holds for the people who may open it; everybody else opens on the
// Dashboard (#43).
//
// THE RAIL DOWN THE LEFT IS THE SOURCE'S NAVIGATION: its six Primary
// Navigation buttons and the three screens behind its hamburger, in
// `shell/NavRail.tsx`. The header carries the rest -- the reference views and
// the fourteen tables -- for Admins and the people an Admin has given the Job
// flag, and `shell/JobMenu.tsx` guards the screens behind it.
//
// NONE OF IT IS DRAWN UNTIL `/api/me` HAS SAID THE PERSON MAY USE IT, and for
// somebody with no access it never is: `shell/AccessGate.tsx` shows them the
// landing page instead.

import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { DashboardScreen } from './dashboards/Dashboard';
import { ChartsScreen } from './views/ChartsScreen';
import {
  ArchiveScreen,
  BarriersDashboardScreen,
  DepartmentScreen,
  HotScreen,
  InterplantTransfersScreen,
  PlantsScreen,
} from './views/barrierScreens';
import { DailyProductionScreen, OrdersScreen, TwoWeekProductionScreen } from './views/productionScreens';
import { CollectionScreen } from './crud/CollectionScreen';
import { RecordScreen } from './crud/RecordScreen';
import { FormScreen } from './crud/FormScreen';
import { DataIndexScreen } from './crud/DataIndexScreen';
import { AdminOnly, AdminScreen } from './admin/AdminScreen';
import { useMe } from './data';
import { account, signOut } from './auth';
import { AboutScreen } from './shell/AboutScreen';
import { AccessGate } from './shell/AccessGate';
import { JobMenu, JobOnly, landingFor } from './shell/JobMenu';
import { NavRail } from './shell/NavRail';
import type { Me } from './types';

// What the header says the signed-in person may do. Read from `access` rather
// than `roles`: a grant an Admin made in the application is not a role.
const ACCESS_LABELS: Record<Me['access'], string> = { admin: 'Admin', user: 'User', none: 'No access' };

export function App() {
  const me = useMe();
  const navigate = useNavigate();
  const signedInAs = account()?.username ?? '';

  return (
    <AccessGate me={me}>
      <div className="app">
        <header className="app-head">
          <button className="brand" onClick={() => navigate('/')}>
            Barrier Tracker
          </button>
          <JobMenu me={me.data} />
          <div className="app-identity">
            <span className="muted">
              {signedInAs}
              {me.data ? ` · ${ACCESS_LABELS[me.data.access]}` : ''}
            </span>
            <button className="link" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </header>

        <div className="app-frame">
          <NavRail admin={Boolean(me.data?.is_admin || me.data?.can_manage_access)} />

          <main className="app-body">
            <Routes>
              {/* The source's default view, for those who may open it. */}
              <Route path="/" element={<Navigate to={landingFor(me.data)} replace />} />

              <Route path="/dashboard" element={<DashboardScreen variant="dashboard" />} />
              <Route path="/daily-production-tracker" element={<DashboardScreen variant="daily" />} />
              <Route path="/charts" element={<ChartsScreen />} />

              <Route path="/barriers/dashboard" element={<BarriersDashboardScreen />} />
              <Route path="/barriers/hot" element={<HotScreen />} />
              <Route path="/barriers/plants" element={<PlantsScreen />} />
              <Route path="/barriers/department" element={<DepartmentScreen />} />
              <Route path="/barriers/archive" element={<ArchiveScreen />} />
              <Route path="/interplant-transfers" element={<JobOnly me={me}><InterplantTransfersScreen /></JobOnly>} />

              <Route path="/two-week-production" element={<TwoWeekProductionScreen />} />
              <Route path="/daily-production" element={<JobOnly me={me}><DailyProductionScreen /></JobOnly>} />
              <Route path="/orders" element={<JobOnly me={me}><OrdersScreen /></JobOnly>} />

              {/* The table index and a table's list are behind the menu; a
                  record, and its new and edit forms, are reached from the
                  rail's screens by everyone. */}
              <Route path="/data" element={<JobOnly me={me}><DataIndexScreen /></JobOnly>} />
              <Route path="/data/:slug" element={<JobOnly me={me}><CollectionScreen /></JobOnly>} />
              <Route path="/data/:slug/new" element={<FormScreen mode="create" />} />
              <Route path="/data/:slug/:id" element={<RecordScreen />} />
              <Route path="/data/:slug/:id/edit" element={<FormScreen mode="edit" />} />

              <Route path="/admin" element={<AdminOnly me={me}><AdminScreen admin={Boolean(me.data?.is_admin)} /></AdminOnly>} />
              <Route path="/about" element={<AboutScreen />} />

              <Route path="*" element={<Navigate to={landingFor(me.data)} replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </AccessGate>
  );
}
